import { i18n } from '@/i18n';
import { defineStore } from 'pinia';
import { reactive } from 'vue';
import { ModalType } from './constants';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { diContainer } from '@/coreContainer/container';
import { LoadingState } from '@/coreDomainsModule/uiLoadingState';
import { initPagingOptions, IPagingOptions } from '@/coreDomainsModule/paging';
import { SortType } from '@/coreInfrastructureModule/axios/generated/inventoryManage';
import { FilterContentItem } from '../filterModal/filterModal';
import { BusinessFactory } from '@/coreDomainsModule/factories/BusinessFactory';
import { GetEditAggregateBusinessChildAdapter } from './adapters/getEditAggregateBusinessChildAdapter';

export const useUpdateBusinessModalStore = defineStore('updateBusinessModal', () => {
  // region Variables
  const t = i18n.t;

  const state = reactive<IUpdateBusinessModalState>(initState());

  // #endregion

  // #region PublicMethods
  /**
   * stateをリセットする
   */
  const $reset = () => {
    Object.assign(state, initState());
  };

  /**
   * 新規追加可能な項目のリストを取得する関数
   * @param isAggregateBusiness - 集計かどうか
   * @returns - 新規追加可能な項目のリスト
   */
  const getAddableOptions = (isAggregateBusiness?: boolean) => {
    if (state.isCreatingBoth && isAggregateBusiness) {
      return [t('manufacturer')];
    }
    return undefined;
  };

  /**
   * APIに集計編集を投げる関数
   */
  const putEditedAggregateBusiness = async (): Promise<ModalType> => {
    if (!state.item) {
      return 'commonError';
    }
    const updatedItem = BusinessFactory.create(state.item);
    const checkDuplicationResponse = await diContainer.checkDuplicateAggregateBusinessUseCase.execute(updatedItem);
    switch (checkDuplicationResponse.status.type) {
      case 'success':
        if (
          checkDuplicationResponse.status.data.aggregateBusinessId &&
          checkDuplicationResponse.status.data.aggregateBusinessId !== updatedItem.businessId
        ) {
          // 重複がある
          return 'duplicateError';
        } else {
          // 重複がないもしくは同一IDのものが重複している（＝キー以外の項目のみ編集している）
          const putResponse = await diContainer.putAggregateBusinessUseCase.execute(updatedItem);
          if (putResponse.status.type === 'success') {
            return 'success';
          }
        }
      case 'error':
      default:
        return 'commonError';
    }
  };

  /**
   * APIに関連取引編集を投げる関数
   */
  const putEditedBaseBusiness = async (): Promise<ModalType> => {
    if (!state.item) {
      return 'commonError';
    }
    const updatedItem = BusinessFactory.create(state.item);
    const checkDuplicationResponse = await diContainer.checkDuplicateBaseBusinessUseCase.execute(updatedItem);
    switch (checkDuplicationResponse.status.type) {
      case 'success':
        if (
          checkDuplicationResponse.status.data.baseBusinessId &&
          checkDuplicationResponse.status.data.baseBusinessId !== updatedItem.businessId
        ) {
          return 'duplicateError';
        } else if (checkDuplicationResponse.status.data.aggregateBusinessId) {
          // 重複がないもしくは同一IDのものが重複している（＝キー以外の項目のみ編集している）かつ、aggregateBusinessIdがある（集計が存在する）
          const putResponse = await diContainer.putBaseBusinessUseCase.execute(updatedItem);
          if (putResponse.status.type === 'success') {
            return 'success';
          }
        }
      case 'error':
      default:
        return 'commonError';
    }
  };

  /**
   * APIに集計作成を投げる関数
   */
  const createNewAggregateBusiness = async (): Promise<ModalType> => {
    if (!state.item) {
      return 'commonError';
    }
    const updatedItem = BusinessFactory.create(state.item);
    const checkDuplicationResponse = await diContainer.checkDuplicateAggregateBusinessUseCase.execute(updatedItem);
    switch (checkDuplicationResponse.status.type) {
      case 'success':
        if (checkDuplicationResponse.status.data.aggregateBusinessId) {
          // 重複がある
          return 'duplicateError';
        } else {
          // 重複がない
          const postResponse = await diContainer.createNewAggregateBusinessUseCase.Execute(updatedItem);
          if (postResponse.status.type === 'success') {
            return 'success';
          }
        }
      case 'error':
      default:
        return 'commonError';
    }
  };

  /**
   * APIに関連取引作成を投げる関数
   */
  const createNewBaseBusiness = async (): Promise<ModalType> => {
    if (!state.item) {
      return 'commonError';
    }
    const updatedItem = BusinessFactory.create(state.item);
    const checkDuplicationResponse = await diContainer.checkDuplicateBaseBusinessUseCase.execute(updatedItem);
    switch (checkDuplicationResponse.status.type) {
      case 'success':
        if (checkDuplicationResponse.status.data.baseBusinessId) {
          // 重複がある
          return 'duplicateError';
        } else if (checkDuplicationResponse.status.data.aggregateBusinessId) {
          // 重複がないかつ、aggregateBusinessIdがある（集計が存在する）

          const parentId = checkDuplicationResponse.status.data.aggregateBusinessId;

          const postResponse = await diContainer.createNewBaseBusinessUseCase.Execute(updatedItem, parentId);
          if (postResponse.status.type === 'success') {
            return 'success';
          }
        } else {
          return 'noParentError';
        }
      case 'error':
      default:
        return 'commonError';
    }
  };

  // =================== 集計子取引 ===================
  /**
   * 集計行の子取引を取得する処理
   * @param sortType - ソートの向き
   * @param sortTarget - ソートの対象カラム
   */
  const fetchChildBusiness = async (sortType: SortType = 'ASC', sortTarget = 'baseBusinessId') => {
    state.childBusiness.loadingState.status.type = 'loading';
    const response = await diContainer.bulkGetBaseBusinessUseCase.execute(
      sortType,
      sortTarget,
      state.childBusiness.pagingState.pageSize,
      state.childBusiness.pagingState.offset,
      _getChildBusinessFilterItem(),
      GetEditAggregateBusinessChildAdapter.adapt
    );
    switch (response.status.type) {
      case 'success':
        state.childBusiness.tableContentItems = _addAggregateBusinessInfoToChildBusiness(response.status.data.result);
        state.childBusiness.pagingState = response.status.data.pagingOptions;
        // 取得した情報数が0の場合、ゼロマッチステータスにする
        if (state.childBusiness.tableContentItems.length === 0) {
          state.childBusiness.loadingState.status.type = 'zeroMatch';
        } else {
          state.childBusiness.loadingState.status.type = 'success';
        }
        break;
      case 'error':
      default:
        state.childBusiness.loadingState.status.type = 'error';
        return null;
    }
  };

  // #endregion

  // #region Private methods

  /**
   * 小取引に集計の情報を付加する処理
   * @param businesses - 子取引の一覧
   */
  const _addAggregateBusinessInfoToChildBusiness = (businesses: IBusiness[]) => {
    return businesses.map(
      (business): IBusiness => ({
        ...business,
        warehouse: state.item?.warehouse ?? '',
        supplierName: state.item?.supplierName ?? '',
        businessNumber: state.item?.businessNumber ?? '',
        businessName: state.item?.businessName ?? '',
        package: state.item?.package ?? '',
      })
    );
  };

  /**
   * 子取引取得時のフィルターを作成して返す
   */
  const _getChildBusinessFilterItem = (): FilterContentItem[] => {
    // フィルター項目は親の必須項目
    return [
      {
        filterType: 'WAREHOUSE_CODE',
        selectedValue: [state.updatedItemDiff?.warehouseNumber ?? state.item?.warehouseNumber ?? ''],
        label: '',
      },
      {
        filterType: 'SUPPLIER_CODE',
        selectedValue: [state.updatedItemDiff?.supplierNumber ?? state.item?.supplierNumber ?? ''],
        label: '',
      },
      {
        filterType: 'BUSINESS_CODE',
        selectedValue: [state.updatedItemDiff?.businessNumber ?? state.item?.businessNumber ?? ''],
        label: '',
      },
      {
        filterType: 'PACKAGE_AMOUNT',
        selectedValue: [state.updatedItemDiff?.package ?? state.item?.package ?? ''],
        label: '',
      },
    ];
  };
  // #endregion

  return {
    state,
    $reset,
    getAddableOptions,
    putEditedAggregateBusiness,
    putEditedBaseBusiness,
    createNewAggregateBusiness,
    createNewBaseBusiness,
    fetchChildBusiness,
  };
});

// #region Interface

/**
 * updateBusinessModalStateのinterface
 * @property item - 編集中の取引情報
 * @property updatedItemDiff - 編集した取引の差分
 * @property isCreateMode - 新規作成かどうか
 * @property isCreatingBoth - 関連取引の新規作成時に集計も新規作成しているかどうか
 * @property modalState - モーダルの開閉状態
 * @property childBusiness - 集計を編集した際に影響を受ける子取引に関する状態
 */
interface IUpdateBusinessModalState {
  item: IBusiness | undefined;
  updatedItemDiff: Partial<IBusiness> | undefined;
  isCreateMode: boolean;
  isCreatingBoth: boolean;
  modalState: {
    aggregateBusinessEdit: boolean;
    baseBusinessEdit: boolean;
  };
  childBusiness: {
    loadingState: LoadingState;
    pagingState: IPagingOptions;
    tableContentItems: any[];
  };
}

/**
 * UpdateBusinessModalStateの初期値を返す
 * @returns - Stateの初期値
 */
const initState = (): IUpdateBusinessModalState => {
  return {
    item: undefined,
    updatedItemDiff: undefined,
    isCreateMode: false,
    isCreatingBoth: false,
    modalState: {
      aggregateBusinessEdit: false,
      baseBusinessEdit: false,
    },
    childBusiness: {
      loadingState: {
        status: {
          type: 'none',
        },
      },
      pagingState: initPagingOptions(50),
      tableContentItems: [],
    },
  };
};

// endregion
